/* getIdToken(true) forces token refresh but can, has, and does cause QUOTA_EXCEEDED */

import { stripPriorityFields } from './utils/priorityAccess.js';

const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

// Patrol id sent as x-patrol-id on patrol-scoped calls. Same pattern as manager/scheduler:
// AuthContext sets it from the signed-in user's profile (patrols[0]) after login.
let _patrolId = null;
export const setPatrolId = (id) => { _patrolId = id; };
export const getPatrolId = () => _patrolId;

let _currentUid = null;
export const setCurrentUid = (uid) => { _currentUid = uid; };

// One attempt to learn the patrol id from the signed-in user's profile. Never throws, never retries.
export const loadPatrolId = async (uid, token) => {
    try {
        const profile = await getUser(uid, token);

        _patrolId = profile?.patrols?.[0] || null;
    } catch {
        _patrolId = null;
    }

    return _patrolId;
};

export const getUser = async (uid, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/${ uid }`, payload);
        const json = response && await response.json();
        
        if (response.ok) {
            return json;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else if (json.error) {
            return { error: json.error };
        } else {
            return { error: 'Failed to fetch user data.' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const getAllUsers = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users`, payload);
        const json = response && await response.json();
        
        if (response.ok) {
            if (Array.isArray(json)) {
                json.sort((a, b) => {
                    const lastNameA = (a.lastName ?? '').toLowerCase();
                    const lastNameB = (b.lastName ?? '').toLowerCase();

                    const cmpLast = lastNameA.localeCompare(lastNameB);
                    if (cmpLast !== 0) return cmpLast;

                    // If last names are equal, sort by first name
                    const firstNameA = (a.firstName ?? '').toLowerCase();
                    const firstNameB = (b.firstName ?? '').toLowerCase();

                    const cmpFirst = firstNameA.localeCompare(firstNameB);
                    if (cmpFirst !== 0) return cmpFirst;

                    // sort by display name if last names and first names are equal
                    const displayNameA = (a.displayName ?? '').toLowerCase();
                    const displayNameB = (b.displayName ?? '').toLowerCase();

                    return displayNameA.localeCompare(displayNameB);
                });
            }

            return json;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else if (json.error) {
            return { error: json.error };
        } else {
            return { error: 'Failed to fetch users data.' };
        }
    } catch (error) {
        if (error.message === 'Failed to fetch') {
            return { error: 'Network error. Please try again later.' };
        }

        return { error: error.message };
    }
};

const NO_PATROL_MSG = 'Unable to identify your patrol. Please log out and log back in.';

// Single lazy attempt to learn the patrol id; never retries or loops.
const ensurePatrolId = async (token) => {
    if (!_patrolId && _currentUid) {
        await loadPatrolId(_currentUid, token);
    }

    return _patrolId;
};

export const addUser = async (user, token) => {
    if (!await ensurePatrolId(token)) {
        return { error: NO_PATROL_MSG };
    }

    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(user)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users`, payload);

        if (response.ok) {
            return true;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else if (response.status === 404) {
            return { error: 'Invalid request' };
        } else {
            const json = await response.json();

            if (json.error === 'patrol/not-a-member' || json.error === 'auth/access-denied') {
                return { error: 'You are not an admin of this patrol.' };
            }

            if (json.error === 'patrol/missing-patrol-id') {
                return { error: NO_PATROL_MSG };
            }

            if (json.error) {
                return json.code ? { error: json.error, code: json.code } : { error: json.error };
            }

            return { error: 'Failed to add user.' };
        }
    } catch (error) {
        console.error('addUser:', error);

        return { error: error.message };
    }
};

// Creates the patroller record for an email that already has a sign-in (see the
// 'core/signin-only-account' code from addUser). One request, no retry.
export const completeUser = async (user, token) => {
    if (!await ensurePatrolId(token)) {
        return { error: NO_PATROL_MSG };
    }

    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(user)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/complete`, payload);

        if (response.ok) {
            return true;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else {
            // 404, 409 and 400 all carry a plain sentence in `error` that is shown as-is.
            let json = {};

            try {
                json = await response.json();
            } catch {
                json = {};
            }

            if (json.error === 'patrol/not-a-member' || json.error === 'auth/access-denied') {
                return { error: 'You are not an admin of this patrol.' };
            }

            if (json.error === 'patrol/missing-patrol-id') {
                return { error: NO_PATROL_MSG };
            }

            return { error: json.error || 'Failed to complete this account.' };
        }
    } catch (error) {
        console.error('completeUser:', error);

        return { error: error.message };
    }
};

export const updateUser = async (user, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(stripPriorityFields(user))
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/${ user.uid }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            if (json.error) {
                if (json.error === 'auth/phone-number-already-exists') {
                    return { error: 'This phone number is already in use.' };
                }
                if (json.error === 'core/forbidden') {
                    return { error: 'You do not have permission to change this profile.' };
                }

                if (json.error === 'core/forbidden-field') {
                    return { error: 'You do not have permission to change one of the fields in this profile.' };
                }

                if (json.error === 'core/account-disabled') {
                    return { error: 'This account is disabled and cannot be changed.' };
                }
            }

            return { error: json.error };
        }
    } catch (error) {
        console.error('updateUser:', error);

        return { error: error.message };
    }
};

export const uploadPhoto = async (uid, photo, token) => {
    try {
        const formData = new FormData();

        formData.append('id', uid);
        formData.append('photo', photo);

        const payload = {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${ token }` },
            body: formData
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/${ uid }/photo`, payload);

        if (response.ok) {
            const json = await response.json();

            return json.photoURL;
        } else {
            const json = await response.json();

            return { error: json.error };
        }
    } catch (error) {
        console.error('uploadPhoto:', error);

        return { error: error.message };
    }
};

export const verifyPhone = async (uid, method, code, identifier, consent, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ code: code, identifier: identifier, consent: consent })
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/verify/${ uid }/${ method }`, payload);

        if (response.ok) {
            const json = await response.json();

            if (json.verified) {
                return ({ verified: true });
            } else if (json.sent) {
                return ({ sent: true });
            } else {
                return ({ error: 'Unknown verification status.' });
            }
        } else {
            const json = await response.json();

            return { error: json.error };
        }
    } catch (error) {
        console.error('verifyPhone:', error);

        return { error: error.message };
    }
};

export const callSubmitGitHubIssue = async (email, type, desc, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'X-System-Source': 'patrol360-core'
            },
            body: JSON.stringify(
                {
                    email: email,
                    desc: desc,
                    type: type
                }
            )
        };

        const response = await fetch(`${ apiEndpoint }/api/submit-github-issue`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return { error: json.error };
        }
    } catch (error) {
        console.error('callSubmitGitHubIssue:', error);

        return { error: error.message };
    }
};
